import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, FolderTree, X } from "lucide-react";
import CoutureLayout from "./CoutureLayout";
import { api, formatApiError, formatPrice } from "../../lib/api";
import { useI18n } from "../../i18n/I18nContext";

export default function CoutureCategories() {
    const { t } = useI18n();
    const [categories, setCategories] = useState([]);
    const [name, setName] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [parentId, setParentId] = useState("");
    const [price, setPrice] = useState("");
    const [costLines, setCostLines] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/couture/categories").then((r) => setCategories(r.data)).catch(() => {});
    useEffect(() => {
        load();
    }, []);

    const addLine = () => setCostLines((l) => [...l, { label: "", amount: "" }]);
    const setLine = (i, key, val) =>
        setCostLines((l) => l.map((x, idx) => (idx === i ? { ...x, [key]: val } : x)));
    const removeLine = (i) => setCostLines((l) => l.filter((_, idx) => idx !== i));

    const costTotal = costLines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
    const profit = (Number(price) || 0) - costTotal;

    const create = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setBusy(true);
        try {
            const cleanLines = costLines
                .filter((l) => l.label.trim() !== "")
                .map((l) => ({ label: l.label.trim(), amount: Number(l.amount) || 0 }));
            await api.post("/couture/categories", {
                name_fr: name.trim(),
                name_en: nameEn.trim(),
                parent_id: parentId || null,
                price_fcfa: price !== "" ? Number(price) : null,
                cost_fcfa: cleanLines.length ? costTotal : null,
                cost_lines: cleanLines.length ? cleanLines : null,
            });
            setName("");
            setNameEn("");
            setParentId("");
            setPrice("");
            setCostLines([]);
            toast.success(t("couture.category_created"));
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/couture/categories/${id}`);
            setCategories((prev) => prev.filter((c) => c.id !== id));
            toast.success(t("couture.deleted"));
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const roots = categories.filter((c) => !c.parent_id);
    const childrenOf = (id) => categories.filter((c) => c.parent_id === id);

    const inputCls =
        "w-full mt-1.5 bg-white border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-brand-gold transition";
    const labelCls = "text-[0.65rem] tracking-[0.22em] uppercase font-bold text-gray-500";

    const renderCat = (c, isSub) => {
        const lines = (c.cost_lines || []).filter((l) => l && l.label);
        const cost = lines.length ? lines.reduce((s, l) => s + (l.amount || 0), 0) : c.cost_fcfa;
        return (
            <div key={c.id} className={`flex items-center justify-between px-5 py-${isSub ? "3 pl-12 border-t border-gray-50" : "4"}`}>
                <div className="flex items-center gap-2 flex-wrap">
                    {!isSub && <FolderTree className="w-4 h-4 text-brand-gold" />}
                    <span className={`font-semibold text-brand-navy ${isSub ? "text-sm" : ""}`}>{c.name_fr}</span>
                    {c.name_en && <span className="text-xs text-gray-400">({c.name_en})</span>}
                    {c.price_fcfa != null && (
                        <span className="text-xs bg-brand-paper border border-gray-200 px-2 py-0.5 text-brand-gold-deep font-semibold" data-testid={`couture-cat-price-${c.id}`}>
                            {Number(c.price_fcfa).toLocaleString("fr-FR")} FCFA
                        </span>
                    )}
                    {cost != null && lines.length > 0 && (
                        <span className="text-xs text-gray-400" data-testid={`couture-cat-cost-${c.id}`}>
                            {t("couture.cost")}: {Number(cost).toLocaleString("fr-FR")} FCFA
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    {lines.length > 0 && (
                        <div className="hidden md:flex gap-1.5 flex-wrap">
                            {lines.map((l, i) => (
                                <span key={i} className="text-[0.62rem] bg-gray-100 text-gray-600 px-2 py-0.5">
                                    {l.label} {Number(l.amount).toLocaleString("fr-FR")}
                                </span>
                            ))}
                        </div>
                    )}
                    <button
                        onClick={() => remove(c.id)}
                        data-testid={`couture-cat-delete-${c.id}`}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <CoutureLayout>
            <div className="p-6 md:p-10 max-w-3xl" data-testid="couture-categories-page">
                <h1 className="font-display text-3xl text-brand-navy">{t("couture.categories")}</h1>
                <div className="w-12 h-px bg-brand-gold mt-2 mb-8" />

                <form onSubmit={create} className="bg-white border border-gray-100 p-6 mb-8">
                    <div className="grid sm:grid-cols-2 gap-5">
                        <label className="block">
                            <span className={labelCls}>{t("couture.category_name_fr")} *</span>
                            <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="couture-cat-name" className={inputCls} />
                        </label>
                        <label className="block">
                            <span className={labelCls}>{t("couture.category_name_en")}</span>
                            <input value={nameEn} onChange={(e) => setNameEn(e.target.value)} data-testid="couture-cat-name-en" className={inputCls} />
                        </label>
                        <label className="block sm:col-span-2">
                            <span className={labelCls}>{t("couture.parent_section")}</span>
                            <select value={parentId} onChange={(e) => setParentId(e.target.value)} data-testid="couture-cat-parent" className={inputCls}>
                                <option value="">{t("couture.no_parent")}</option>
                                {roots.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name_fr}</option>
                                ))}
                            </select>
                        </label>
                        <label className="block">
                            <span className={labelCls}>{t("couture.cat_price")}</span>
                            <input type="number" min="0" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="couture-cat-price" className={inputCls} placeholder="FCFA" />
                        </label>
                    </div>

                    {/* Cost lines */}
                    <div className="mt-5">
                        <div className="flex items-center justify-between mb-2">
                            <span className={labelCls}>{t("couture.cost_lines")}</span>
                            <button type="button" onClick={addLine} data-testid="couture-cat-add-line" className="inline-flex items-center gap-1.5 text-xs text-brand-gold hover:text-brand-gold-deep font-semibold uppercase tracking-wider">
                                <Plus className="w-3.5 h-3.5" /> {t("couture.add_cost_line")}
                            </button>
                        </div>
                        {costLines.length === 0 && (
                            <p className="text-xs text-gray-400">{t("couture.cost_lines_hint")}</p>
                        )}
                        {costLines.map((l, i) => (
                            <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-2 mb-2 items-center">
                                <input value={l.label} onChange={(e) => setLine(i, "label", e.target.value)} placeholder={t("couture.cost_label_ph")} data-testid={`couture-cat-line-label-${i}`} className={inputCls} />
                                <input type="number" min="0" value={l.amount} onChange={(e) => setLine(i, "amount", e.target.value)} placeholder="FCFA" data-testid={`couture-cat-line-amount-${i}`} className={inputCls + " w-32"} />
                                <button type="button" onClick={() => removeLine(i)} className="p-2 text-gray-400 hover:text-red-500">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                        {costLines.length > 0 && (
                            <div className="mt-3 flex items-center justify-between bg-brand-paper border border-gray-100 px-4 py-2.5" data-testid="couture-cat-profit-preview">
                                <span className="text-[0.62rem] tracking-[0.18em] uppercase font-bold text-gray-500">
                                    {t("couture.cost")}: {formatPrice(costTotal, "FCFA")} · {t("couture.profit")}
                                </span>
                                <span className={`font-display ${profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                                    {formatPrice(profit, "FCFA")}
                                </span>
                            </div>
                        )}
                    </div>

                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="couture-cat-submit"
                        className="mt-5 inline-flex items-center gap-2 px-6 py-3 bg-brand-gold hover:bg-brand-gold-deep disabled:opacity-60 text-white text-[0.72rem] tracking-[0.22em] uppercase font-semibold transition-colors"
                    >
                        <Plus className="w-4 h-4" /> {t("couture.add_category")}
                    </button>
                </form>

                <div className="space-y-3" data-testid="couture-categories-list">
                    {roots.length === 0 && (
                        <div className="bg-white border border-gray-100 p-8 text-center text-gray-400">{t("couture.no_categories")}</div>
                    )}
                    {roots.map((c) => (
                        <div key={c.id} className="bg-white border border-gray-100">
                            {renderCat(c, false)}
                            {childrenOf(c.id).map((sub) => renderCat(sub, true))}
                        </div>
                    ))}
                </div>
            </div>
        </CoutureLayout>
    );
}
